import queryString from 'query-string';

/* The words an interpolated empty value leaves behind.

   A link built by dropping a missing value into a template -
   `` `/parts?maker_id=${maker_id}` `` with nothing in `maker_id` - comes out
   carrying the word `undefined`, and `query-string` hands it back as exactly
   that: a five-letter string that is perfectly truthy.

   The models page built every tile link that way, so a client who reached
   /models without a maker got a screenful of links to
   `/parts?maker_id=undefined&model_id=12`. Guarding that page stops new ones
   being made; it does nothing for the ones already in bookmarks and chat
   histories. Followed today, `maker_id=undefined` is sent to the API and
   compared against an integer column, which reads it as 0 - so the client gets
   an empty parts table for a model that really does have parts, with nothing
   on screen to say why. */
const INTERPOLATED_EMPTY = ['undefined', 'null'];

/* Only the id parameters are held to that, because they are the only ones this
   app builds by interpolation. The listing also carries free text - a name, a
   part code - and someone really can search for the word `undefined`. Dropping
   it there would leave the search box quietly ignoring a term the client typed,
   which is a worse bug than the one being fixed. */
const isInterpolatedEmpty = (key, value) => key.endsWith('_id') && INTERPOLATED_EMPTY.includes(value);

/* A key written twice - `?maker_id=1&maker_id=2` - comes back from
   `query-string` as an array rather than a string, so it is narrowed to its
   last value here. Rails reads a repeated scalar key the same way, which means
   the screen now filters by the value the API was always going to answer for
   instead of disagreeing with it.

   Left as an array it would slip past the rules below rather than meet them:
   the emptiness check reads `['undefined', 'undefined']` as the string
   `'undefined,undefined'`, which is neither blank nor one of the words above.
   A stale link that picked up its `maker_id` twice would keep the key, and the
   array would go on to be spread into the request - so the doubled form of the
   exact URL this exists to catch was getting through it. */
const singleValue = (value) => (Array.isArray(value) ? value[value.length - 1] : value);

/* Parse a location's search string and drop the keys that carry nothing.

   Deciding not to filter by a value is only half of it - the value also has to
   stay out of the request. The parts listing sends the whole parsed query on to
   the API, so a `maker_id` left in it returns an empty response however sound
   the rest of the URL is. Removed here, a stale
   `/parts?maker_id=undefined&model_id=12` is exactly the `/parts?model_id=12`
   it should always have been.

   Values are otherwise left as they arrive - beyond picking one out of a
   repeated key, this drops keys rather than coercing what they hold, and `'0'`
   is a value like any other. */
export const parseQuery = (search) => {
  const parsed = queryString.parse(search);

  return Object.keys(parsed).reduce((query, key) => {
    const single = singleValue(parsed[key]);
    const value = String(single ?? '').trim();

    if (value !== '' && !isInterpolatedEmpty(key, value)) query[key] = single;

    return query;
  }, {});
};
